import {apiDelete, apiGet, apiPost, apiPut} from './client'
import {formatDate, newHandle, nextGrampsId, parseDate, type DateVal} from './gramps'  // newHandle/nextGrampsId — реэкспорт для остального кода
import {EVENT_SKELETON, eventIsEmpty, eventPatch, EV_BIRTH, EV_DEATH, type EventEntry} from './events'
import {emptyName, nameBody, nameFromRaw, nameIsEmpty, type NameEntry, type NameRaw} from './names'
import {EVENT_OCCUPATION, jobIsEmpty, jobPatch, type JobEntry} from './jobs'
import {mediaRef, uploadPhoto} from './media'

/* Запись идёт ПООБЪЕКТНО через /api/people/ и /api/events/ — не через
 * /api/transactions/.
 *
 * Транзакции требуют поле `old` и сверяют его с базой. Но GET отдаёт объект
 * в одной сериализации (emit.extract_object: свойства + __dict__, с подменами
 * вроде rect: null -> []), а сверка идёт против object_to_dict — другой.
 * Поэтому «было», взятое из GET, не совпадает НИКОГДА, и любая правка
 * отбивалась с «Object has changed». Воспроизведено на живом сервере:
 * транзакция с old == new == ответ GET даёт 400, тот же объект через PUT — 200.
 *
 * Цена отказа от транзакций — потеря атомарности: человек и его события
 * пишутся раздельно. Поэтому события создаются ДО правки персоны, а удаляются
 * ПОСЛЕ: в худшем случае останется неприкаянное событие, а не персона,
 * ссылающаяся в пустоту. */

const EVENT_BIRTH = EV_BIRTH
const EVENT_DEATH = EV_DEATH
const ROLE_PRIMARY = 1

const eventType = (v: number) => ({_class: 'EventType', value: v, string: ''})
const eventRole = (v: number) => ({_class: 'EventRoleType', value: v, string: ''})

export {parseDate, formatDate, newHandle, nextGrampsId}
export type {DateVal}

const makeDate = (dv: DateVal) => ({
  _class: 'Date',
  calendar: 0,
  modifier: 0,
  quality: 0,
  dateval: dv,
  text: '',
  sortval: 0,
  newyear: 0,
})

/** Что делаем с главным фото: ничего, заменить или убрать. */
export interface PhotoChange {
  blob?: Blob | null
  remove?: boolean
}

/* Правка человека идёт по модели самого Gramps: у персоны нет полей
 * «фамилия» и «дата рождения» — есть СПИСОК имён и СПИСОК событий.
 * Раньше форма была плоской (фамилия, имя, рождение, смерть, погребение),
 * и всё, что в неё не влезало, правке не поддавалось: третье имя, дата
 * проживания, служба. Список ложится на базу один в один. */
export interface PersonForm {
  gender: number
  /** первое — основное (primary_name), остальные — alternate_names */
  names: NameEntry[]
}

/** Быстрое заведение человека — отдельная и намеренно простая форма.
 *  При создании нужны фамилия, имя и, если известно, даты; остальное
 *  дозаполняется в карточке. */
export interface NewPersonForm {
  surname: string
  /** Имя и отчество одним полем — ровно так их положил импорт GEDCOM. */
  given: string
  gender: number
  birth: string
  death: string
  /** отметка «человека нет» — она, а не дата, решает.
   *  Даты смерти часто неизвестны, а факт известен. */
  deceased: boolean
  /** handle места из справочника (не текст: место в Gramps — отдельный объект) */
  birthPlace: string | null
  deathPlace: string | null
}

interface RawPerson {
  handle: string
  gender: number
  primary_name: Record<string, unknown>
  event_ref_list?: {ref: string; [k: string]: unknown}[]
  birth_ref_index?: number
  death_ref_index?: number
  media_list?: {ref: string; rect?: number[] | null}[]
  [k: string]: unknown
}

export function formFromPerson(p: RawPerson): PersonForm {
  return {
    gender: p.gender,
    names: [
      nameFromRaw(p.primary_name as NameRaw),
      ...((p.alternate_names as NameRaw[] | undefined) || []).map(nameFromRaw),
    ],
  }
}

const eventRef = (ref: string) => ({
  _class: 'EventRef',
  ref,
  role: eventRole(ROLE_PRIMARY),
  private: false,
  citation_list: [],
  note_list: [],
  attribute_list: [],
})

/** Создаёт объект и возвращает handle, назначенный сервером.
 *  Ни handle, ни gramps_id клиент не придумывает: сервер делает это сам
 *  и без коллизий. */
async function createObject(path: string, body: unknown, cls: string): Promise<string> {
  const res = await apiPost<{handle?: string; _class?: string}[]>(path, body)
  const h = (res || []).find(i => i._class === cls)?.handle || res?.[0]?.handle
  if (!h) throw new Error("Сервер не вернул идентификатор созданной записи")
  return h
}

const bareEvent = (typeValue: number, dv: DateVal | null, place: string | null = null) => ({
  _class: 'Event',
  type: eventType(typeValue),
  date: makeDate(dv || [0, 0, 0, false]),
  place,
  description: '',
  private: false,
  tag_list: [],
  citation_list: [],
  note_list: [],
  media_list: [],
  attribute_list: [],
  change: 0,
})

/* Нужны в relations.ts: дата свадьбы — событие семьи, а не человека,
   но собирается теми же кирпичами. */
export {createObject, eventRef, bareEvent}

/** Событие сносим, только если на него больше никто не ссылается:
 *  события в Gramps бывают общими. */
async function deleteIfOrphan(evHandle: string, exceptPerson: string) {
  const ev = await apiGet<Record<string, unknown>>(`/events/${evHandle}`, {backlinks: 1})
  const bl = (ev.backlinks || {}) as Record<string, string[]>
  const others = Object.values(bl).flat().filter(x => x !== exceptPerson)
  if (!others.length) await apiDelete(`/events/${evHandle}`)
}

/** Сохранение человека: имена, пол, события, места работы, фото. */
export async function savePerson(
  handle: string,
  form: PersonForm,
  jobs: JobEntry[] = [],
  jobsWas: JobEntry[] = [],
  photo: PhotoChange = {},
  /** уже созданные объекты, которые надо прицепить к персоне */
  extra: {
    addNotes?: string[]
    delNotes?: string[]
    addMedia?: string[]
    dropMedia?: string[]
    /** все события человека: рождение, смерть, проживание, служба и прочее */
    events?: EventEntry[]
    eventsWas?: EventEntry[]
    /** какой снимок сделать главным (он же аватарка) */
    mainMedia?: string
    /** какую часть главного снимка показывать: [x1,y1,x2,y2] в процентах */
    mainRect?: number[] | null
  } = {},
): Promise<void> {
  const person = await apiGet<RawPerson>(`/people/${handle}`)
  const next = JSON.parse(JSON.stringify(person)) as RawPerson
  next.gender = form.gender

  /* ---------- имена ----------
   * Первое в списке — основное, прочие идут в alternate_names. Пустые
   * строки отбрасываем: пустое имя в Gramps валидно, но в списке людей
   * выглядит как потерянная запись. */
  {
    const kept = form.names.filter((n, i) => i === 0 || !nameIsEmpty(n))
    next.primary_name = nameBody(kept[0] || emptyName(2))
    next.alternate_names = kept.slice(1).map(nameBody)
  }

  const dropped = new Set<string>()

  /* ---------- события ----------
   * Все — одним механизмом: правим изменившиеся, создаём новые, пропавшие
   * помечаем к сносу. Рождение и смерть тут же, наравне с остальными:
   * в Gramps это обычные события, а указатели на них пересчитываются
   * по типу уже после сборки списка. */
  const added: string[] = []
  /** какой handle получила только что созданная строка формы */
  const born = new Map<object, string>()
  const evType = new Map<string, number>()
  for (const e of extra.eventsWas || []) if (e.handle) evType.set(e.handle, e.type)
  for (const e of extra.events || []) if (e.handle) evType.set(e.handle, e.type)

  async function syncSpans<T extends {handle: string | null}>(
    list: T[],
    was: T[],
    isEmpty: (x: T) => boolean,
    /** только редактируемые поля: накладываются на прочитанный объект */
    patch: (x: T) => object,
    /** запомнить тип созданного события: по нему потом ищутся указатели */
    typeOf: (x: T, handle: string) => void,
  ) {
    const wasByHandle = new Map(was.filter(x => x.handle).map(x => [x.handle!, x]))
    const keep = new Set(list.filter(x => x.handle).map(x => x.handle!))
    for (const x of list) {
      if (!x.handle || isEmpty(x)) continue
      const before = wasByHandle.get(x.handle)
      if (before && JSON.stringify(before) === JSON.stringify(x)) continue
      const ev = await apiGet<Record<string, unknown>>(`/events/${x.handle}`)
      await apiPut(`/events/${x.handle}`, {...ev, ...patch(x)})
    }
    for (const x of list) {
      if (x.handle || isEmpty(x)) continue
      const h = await createObject('/events/', {...EVENT_SKELETON, place: null, ...patch(x)}, 'Event')
      added.push(h)
      born.set(x, h)
      typeOf(x, h)
    }
    for (const h of wasByHandle.keys()) if (!keep.has(h)) dropped.add(h)
  }

  await syncSpans(jobs, jobsWas, jobIsEmpty, jobPatch, (_, h) => evType.set(h, EVENT_OCCUPATION))
  await syncSpans(extra.events || [], extra.eventsWas || [], eventIsEmpty, eventPatch,
                  (x, h) => evType.set(h, x.type))

  /* ---------- список ссылок ----------
   * Порядок берём из формы: в Gramps события можно двигать вверх и вниз,
   * и этот порядок и есть event_ref_list. Сама ссылка (EventRef) при этом
   * сохраняется прежняя — в ней роль и признак приватности.
   *
   * birth_ref_index и death_ref_index — ПОЗИЦИИ в этом списке, а не
   * ссылки: любая перестановка или удаление их ломает. Поэтому список
   * собирается сначала, а указатели ищутся в нём по типу события —
   * разъехаться они уже не могут. */
  {
    const wasRef = new Map((person.event_ref_list || []).map(r => [r.ref, r]))
    const handleOf = (x: {handle: string | null}) => x.handle || born.get(x) || ''
    const wanted = [...(extra.events || []), ...jobs]
      .map(handleOf)
      .filter(h => h && !dropped.has(h))
    // то, чего не было в форме, дописываем в хвост: не показали — не значит
    // «удалить», и молча отвязывать такое нельзя
    const known = new Set(wanted)
    const leftovers = (person.event_ref_list || [])
      .map(r => r.ref)
      .filter(h => !known.has(h) && !dropped.has(h))
    next.event_ref_list = [...wanted, ...leftovers].map(h => wasRef.get(h) || eventRef(h))
  }

  const locate = (t: number) => next.event_ref_list!.findIndex(r => evType.get(r.ref) === t)
  next.birth_ref_index = locate(EV_BIRTH)
  next.death_ref_index = locate(EV_DEATH)

  /* ---------- главное фото ----------
   * Правим только первый элемент media_list — он и показывается везде.
   * Остальные снимки не трогаем: стирать то, чего пользователь не видел
   * в форме, нельзя. */
  if (photo.blob || photo.remove) {
    const rest = person.media_list?.slice(1) || []
    next.media_list = photo.blob ? [mediaRef(await uploadPhoto(photo.blob)), ...rest] : rest
  }

  if (extra.addNotes?.length || extra.delNotes?.length) {
    const gone = new Set(extra.delNotes || [])
    // саму заметку не удаляем: она может быть прицеплена и к событию,
    // и к семье — отвязываем только от этого человека
    next.note_list = [
      ...((person.note_list as string[] | undefined) || []).filter(h => !gone.has(h)),
      ...(extra.addNotes || []),
    ]
  }
  /* Снимок только отвязываем от человека — сам объект и файл остаются
     в архиве. Удаление медиа насовсем — отдельное действие в галерее:
     фото часто снимают не с того человека, и потерять его при этом
     нельзя. */
  if (extra.dropMedia?.length) {
    const gone = new Set(extra.dropMedia)
    next.media_list = (next.media_list || []).filter(r => !gone.has(r.ref))
  }
  if (extra.addMedia?.length) {
    next.media_list = [...(next.media_list || []), ...extra.addMedia.map(mediaRef)]
  }

  /* ---------- главное фото и его область ----------
   * Главное — просто первое в media_list: так его берёт и наш интерфейс,
   * и штатный Gramps. Поэтому «сделать главным» — это перестановка, а не
   * отдельное поле; удаление снимка поэтому же молча меняло аватарку на
   * случайную, и теперь есть чем это поправить.
   *
   * Область (rect) живёт в ССЫЛКЕ, а не в самом снимке: на групповой
   * фотографии у каждого своё лицо, и обрезка у каждого своя. */
  if (extra.mainMedia || extra.mainRect !== undefined) {
    const list = [...(next.media_list || [])]
    const want = extra.mainMedia || list[0]?.ref
    const i = list.findIndex(r => r.ref === want)
    if (i >= 0) {
      const [ref] = list.splice(i, 1)
      const rect = extra.mainRect === undefined ? ref.rect : extra.mainRect
      list.unshift({...ref, rect: rect && rect.length === 4 ? rect : null})
      next.media_list = list
    }
  }

  await apiPut(`/people/${handle}`, next)

  // события сносим последними — когда персона на них уже не ссылается
  for (const h of dropped) await deleteIfOrphan(h, handle)
}

/** Создание человека: сначала события, затем персона. Возвращает её handle. */
export async function createPerson(form: NewPersonForm): Promise<string> {
  const refs: ReturnType<typeof eventRef>[] = []
  let birthIdx = -1
  let deathIdx = -1

  const addEvent = async (kind: 'birth' | 'death', value: string) => {
    const dv = parseDate(value)
    if (kind === 'birth' ? !dv : !form.deceased) return
    const h = await createObject('/events/', bareEvent(kind === 'birth' ? EVENT_BIRTH : EVENT_DEATH, dv, kind === 'birth' ? form.birthPlace : form.deathPlace), 'Event')
    refs.push(eventRef(h))
    if (kind === 'birth') birthIdx = refs.length - 1
    else deathIdx = refs.length - 1
  }

  await addEvent('birth', form.birth)
  await addEvent('death', form.death)

  return createObject('/people/', {
    _class: 'Person',
    gender: form.gender,
    primary_name: {
      _class: 'Name',
      first_name: form.given.trim(),
      surname_list: [
        {
          _class: 'Surname',
          surname: form.surname.trim(),
          prefix: '',
          primary: true,
          connector: '',
          origintype: {_class: 'NameOriginType', value: 1, string: ''},
        },
      ],
      suffix: '',
      title: '',
      call: '',
      nick: '',
      type: {_class: 'NameType', value: 2, string: ''},
      private: false,
      date: makeDate([0, 0, 0, false]),
      citation_list: [],
      note_list: [],
      group_as: '',
      sort_as: 0,
      display_as: 0,
      famnick: '',
    },
    alternate_names: [],
    event_ref_list: refs,
    birth_ref_index: birthIdx,
    death_ref_index: deathIdx,
    family_list: [],
    parent_family_list: [],
    media_list: [],
    address_list: [],
    attribute_list: [],
    urls: [],
    lds_ord_list: [],
    citation_list: [],
    note_list: [],
    person_ref_list: [],
    tag_list: [],
    private: false,
  }, 'Person')
}

export const emptyPersonForm = (): NewPersonForm => ({
  surname: '',
  given: '',
  gender: 2,
  birth: '',
  death: '',
  deceased: false,
  birthPlace: null,
  deathPlace: null,
})
